import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import React, { useEffect } from "react";
import {
  PricingType,
  ProductType,
} from "../../../../models/features/Integration/DmsSetup";
import {
  useSetPriceSyncingTypeMutation,
  useGetAvailablePriceTypesQuery,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducer/rootReducer";
import { usePriceOptions } from "../../../../services/utilities/utility";

export interface PriceRuleProps {
  isOpen: boolean;
  handleClose: (refreshData: boolean) => void;
  productType: ProductType;
  productIds: string[];
}

const PriceRule: React.FunctionComponent<PriceRuleProps> = ({
  handleClose,
  isOpen,
  productType,
  productIds,
}) => {
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const priceOptions = usePriceOptions();
  const [priceType, setPriceType] = React.useState<PricingType>();
  const [defaultPriceType, setDefaultPriceType] = React.useState<PricingType>(
    PricingType.Price
  );
  const [applyRuleConfirm, setApplyRuleConfirm] =
    React.useState<boolean>(false);
  const [availablePriceOptions, seAvailablePriceOptions] = React.useState<
    PricingType[]
  >([]);
  const [isErrorFound, setIsErrorFound] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const { data, isLoading } = useGetAvailablePriceTypesQuery({
    producType: productType,
  });

  const [setPriceSyncingMutation, setPriceSyncingMutationResult] =
    useSetPriceSyncingTypeMutation();

  React.useEffect(() => {
    if (productType === ProductType.Wholegoods) {
      const enumValue =
        PricingType[dmsSetup?.priceType.toString() as keyof typeof PricingType];
      setDefaultPriceType(enumValue);
    } else {
      const enumValue =
        PricingType[
          dmsSetup?.partsPriceType.toString() as keyof typeof PricingType
        ];
      setDefaultPriceType(enumValue);
    }
  }, []);

  const handleCloseConfirmation = () => {
    setApplyRuleConfirm(false);
  };

  const handleApplyPriceRule = () => {
    setIsErrorFound(false);
    setErrorMessage("");
    setPriceSyncingMutation({
      pricing: priceType ?? PricingType.Price,
      productType: productType,
      syncAll: false,
      productIds: productIds,
    }).then((res: any) => {
      if (res?.data && res?.data?.status === 200) {
        setApplyRuleConfirm(false);
        handleClose(true);
      } else {
        setIsErrorFound(true);
        if (res?.data && res?.data?.status === 208) {
          setErrorMessage(
            "A bulk operation for price updates is already running. Please wait for a while to complete it!"
          );
        } else if (res?.data?.status === 400) {
          setErrorMessage(
            "Found error while updating prices. Please contact to the support!"
          );
        } else {
          setErrorMessage("Something went wrong. Please try again in a while!");
        }
      }
    });
  };
  const handlePriceTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceType(Number(event.target.value) as PricingType);
  };

  useEffect(() => {
    if (data && data?.entityList && data?.entityList?.length > 0) {
      const allowedTypeNumbers = data.entityList
        .map((name: string) => PricingType[name as keyof typeof PricingType])
        .filter((val): val is PricingType => val !== undefined);
      seAvailablePriceOptions(allowedTypeNumbers);
    }
  }, [data]);

  return (
    <>
      <Dialog
        fullWidth
        open={isOpen}
        onClose={() => {
          handleClose(false);
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Products Level Price Setting
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: "auto" }}>
          <Typography>
            Select any price from the followings to apply.
          </Typography>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <FormControl>
              <RadioGroup
                name="price-type-options"
                value={priceType}
                onChange={handlePriceTypeChange}
              >
                {priceOptions
                  .filter((x) => availablePriceOptions.includes(x.type))
                  .map((option) => (
                    <FormControlLabel
                      key={option.key}
                      value={option.type}
                      control={<Radio />}
                      label={
                        <Typography fontSize="0.9rem">
                          {option.label}{" "}
                          {option.type === defaultPriceType && (
                            <Typography component="span" fontWeight="bold">
                              (Current Default)
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          )}
          <Box mt={1}>
            <Alert severity="warning">
              The products with zero{" "}
              {dmsSetup?.dmsLocations[0].dmsType === 0
                ? "web price"
                : "price level"}{" "}
              will have the <b>MSRP</b> by default.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            autoFocus
            onClick={() => {
              handleClose(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setApplyRuleConfirm(true);
            }}
            disabled={priceType === undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        open={applyRuleConfirm}
        onClose={handleCloseConfirmation}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          <Typography color="primary" sx={{ fontWeight: "bold" }}>
            Confirm to Apply
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure to apply this price to selected products?
          </Typography>

          <Alert severity="info" sx={{ fontSize: "small" }}>
            Price updates on Shopify may take 1–2 minutes, depending on the
            number of products.
          </Alert>
          {isErrorFound ? (
            <Alert sx={{ mt: 1 }} severity="warning">
              {errorMessage}
            </Alert>
          ) : (
            ""
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            autoFocus
            onClick={() => {
              setApplyRuleConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={setPriceSyncingMutationResult.isLoading}
            onClick={handleApplyPriceRule}
          >
            {setPriceSyncingMutationResult.isLoading ? (
              <CircularProgress size="1.2rem" color="inherit" />
            ) : (
              "Yes"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PriceRule;
